import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import Button from "@/components/foundation/Button";
import { submitMarketplaceWaitlist } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * MarketplaceWaitlistForm — email + business name capture for the marketplace waitlist.
 * Phase 4 will wire to POST /api/marketplace-waitlist.
 */
export const MarketplaceWaitlistForm = ({
  onSubmit,
  source = "marketplace_waitlist",
  className,
  "data-testid": testId = "marketplace-waitlist-form",
}) => {
  const [email, setEmail] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === "submitting" || status === "success") return;

    const next = {};
    if (!email.trim()) next.email = "Business email is required.";
    else if (!EMAIL_RE.test(email.trim())) next.email = "Enter a valid email.";
    if (!businessName.trim()) next.businessName = "Business name is required.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setStatus("submitting");
    const payload = {
      business_email: email.trim(),
      business_name: businessName.trim(),
      source,
    };
    try {
      if (onSubmit) {
        await onSubmit(payload);
      } else {
        await submitMarketplaceWaitlist(payload);
      }
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setErrors({ _root: err?.message || "Something went wrong. Please try again in a moment." });
    }
  };

  return (
    <div className={cn("w-full", className)} data-testid={testId}>
      <AnimatePresence mode="wait" initial={false}>
        {status === "success" ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="flex items-start gap-4 rounded-2xl border border-brand-emerald/25 bg-brand-emerald/8 p-6"
            data-testid="form-success"
            role="status"
            aria-live="polite"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-emerald text-black">
              <Check className="h-5 w-5" strokeWidth={3} />
            </span>
            <div>
              <h4 className="font-display text-lg font-semibold text-white">
                You&rsquo;re on the list.
              </h4>
              <p className="mt-1 text-sm text-text-secondary">
                We&rsquo;ll be in touch as the marketplace opens.
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.form
            key="form"
            onSubmit={handleSubmit}
            noValidate
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <label className="flex flex-col">
                <span className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-text-muted">
                  Business Email <span className="text-brand-emerald">*</span>
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors((s) => ({ ...s, email: undefined }));
                  }}
                  placeholder="founder@company.com"
                  aria-invalid={Boolean(errors.email)}
                  className={cn(
                    "w-full rounded-xl border bg-white/[0.02] px-4 py-3 text-sm text-white placeholder:text-text-muted outline-none transition-colors focus:border-brand-emerald/60 focus:bg-white/[0.04]",
                    errors.email
                      ? "border-red-500/60"
                      : "border-white/[0.08] hover:border-white/[0.16]",
                  )}
                  data-testid="mw-email"
                />
                {errors.email && (
                  <span className="mt-1.5 pl-1 text-xs text-red-400">
                    {errors.email}
                  </span>
                )}
              </label>
              <label className="flex flex-col">
                <span className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.12em] text-text-muted">
                  Business Name <span className="text-brand-emerald">*</span>
                </span>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => {
                    setBusinessName(e.target.value);
                    if (errors.businessName)
                      setErrors((s) => ({ ...s, businessName: undefined }));
                  }}
                  placeholder="Acme Coffee Co."
                  aria-invalid={Boolean(errors.businessName)}
                  className={cn(
                    "w-full rounded-xl border bg-white/[0.02] px-4 py-3 text-sm text-white placeholder:text-text-muted outline-none transition-colors focus:border-brand-emerald/60 focus:bg-white/[0.04]",
                    errors.businessName
                      ? "border-red-500/60"
                      : "border-white/[0.08] hover:border-white/[0.16]",
                  )}
                  data-testid="mw-business-name"
                />
                {errors.businessName && (
                  <span className="mt-1.5 pl-1 text-xs text-red-400">
                    {errors.businessName}
                  </span>
                )}
              </label>
            </div>

            {errors._root && (
              <p className="text-xs text-red-400" role="alert">
                {errors._root}
              </p>
            )}

            <div className="mt-2 flex items-center justify-between gap-4">
              <p className="text-xs text-text-muted">
                No spam. Unsubscribe anytime.
              </p>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={status === "submitting"}
                rightIcon={
                  status === "submitting" ? null : <ArrowRight className="h-4 w-4" />
                }
                data-testid="mw-submit-btn"
              >
                {status === "submitting" ? "Submitting…" : "Join Waitlist"}
              </Button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MarketplaceWaitlistForm;
